import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addKnowledgeBaseDocuments,
  createKnowledgeBase,
  deleteKnowledgeBase,
  deleteKnowledgeBaseDocument,
  getKnowledgeBase,
  getKnowledgeBaseDocuments,
  getSingleKnowledgeBase,
  stopKnowledgeBase,
  updateSingleKnowledgeBase,
} from "../Api/knowledge";
import {
  Document as KnowledgeBaseDocument,
  GetAllKnowledgeBasesResponse,
  GetSingleKnowledgeBaseResponse,
} from "../Models/knowledge";
import { AxiosError } from "axios";
import { KnowledgeBaseStatus, ProcessingStatus } from "../Enums/companyStatus";
import { Pipeline } from "../Enums/pipeline";

export const useKnowledge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newKnowledgeBase: {
      name: string;
      description: string;
      files: File[];
      pipeline: Pipeline;
    }) =>
      createKnowledgeBase(
        newKnowledgeBase.name,
        newKnowledgeBase.description,
        newKnowledgeBase.files,
        newKnowledgeBase.pipeline
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledgeList"] });
    },
  });
};

export const useKnowledgeList = () => {
  return useQuery<GetAllKnowledgeBasesResponse[], Error>({
    queryKey: ["knowledgeList"],
    queryFn: () => getKnowledgeBase(),
    refetchInterval: (query) => {
      if (!query?.state?.data) return false;

      const pending = query.state.data.some(
        (kb: GetAllKnowledgeBasesResponse) =>
          kb.status === KnowledgeBaseStatus.WAITING ||
          kb.status === KnowledgeBaseStatus.IN_PROGRESS
      );

      if (!pending) return false;

      return 2000;
    },
  });
};

export const useSingleKnowledge = (knowledgeBaseId: number) => {
  return useQuery<GetSingleKnowledgeBaseResponse, AxiosError>({
    queryKey: ["singleKnowledgeBase", knowledgeBaseId],
    queryFn: () => getSingleKnowledgeBase(knowledgeBaseId),
    enabled: !!knowledgeBaseId,
    refetchInterval: 15000,
  });
};

export const useUpdateKnowledge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      knowledgeBaseId: number | null;
      name: string;
      description: string;
    }) =>
      updateSingleKnowledgeBase(
        params.knowledgeBaseId,
        params.name,
        params.description
      ),
    onSuccess: (_, { knowledgeBaseId }) => {
      queryClient.invalidateQueries({ queryKey: ["knowledgeList"] });
      queryClient.invalidateQueries({
        queryKey: ["singleKnowledgeBase", knowledgeBaseId],
      });
    },
  });
};

export const useDeleteKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteKnowledgeBase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledgeList"] });
    },
  });
};

export const useKnowledgeBaseDocuments = (
  knowledgeBaseId: number,
  enabled: boolean = true
) => {
  return useQuery<KnowledgeBaseDocument[], AxiosError>({
    queryKey: ["kbDocuments", knowledgeBaseId],
    queryFn: () => getKnowledgeBaseDocuments(knowledgeBaseId),
    enabled: enabled && !!knowledgeBaseId,
    refetchInterval: (query) => {
      const pending = query?.state?.data?.some(
        (doc: KnowledgeBaseDocument) =>
          doc.status === ProcessingStatus.WAITING ||
          doc.status === ProcessingStatus.IN_PROGRESS
      );

      return pending ? 2000 : false;
    },
  });
};

export const useAddKnowledgeBaseDocuments = (knowledgeBaseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { files: File[]; pipeline: Pipeline }) =>
      addKnowledgeBaseDocuments(
        knowledgeBaseId,
        params.files,
        params.pipeline
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["kbDocuments", knowledgeBaseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["singleKnowledgeBase", knowledgeBaseId],
      });
      queryClient.invalidateQueries({ queryKey: ["knowledgeList"] });
    },
  });
};

export const useDeleteKnowledgeBaseDocument = (knowledgeBaseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) => deleteKnowledgeBaseDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["kbDocuments", knowledgeBaseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["singleKnowledgeBase", knowledgeBaseId],
      });
    },
  });
};

export const useStopKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => stopKnowledgeBase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledgeList"] });
    },
  });
};
